import React from "react";
import BaseField from "./BaseField";

export default function LocationField({
  field,
  value,
  setValue,
  renderInlineChildren,
  openViewer,
  previewMode = false,
}) {
  const required = !!field.required;
  const label = field.label || "Location";
  const mode = field.config?.mode || "manual"; // manual | gps | preset

  if (mode === "gps") {
    return (
      <BaseField
        field={field}
        value={value}
        setValue={setValue}
        openViewer={openViewer}
        renderInlineChildren={renderInlineChildren}
        previewMode={previewMode}
      >
        <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
          {label} {required && <span className="text-orange-500">*</span>}
        </h3>

        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                  const { latitude, longitude } = position.coords;
                  setValue({
                    ...(value || {}),
                    latitude,
                    longitude,
                  });
                });
              }
            }}
            className="px-4 py-3 rounded-lg border-2 border-orange-500 bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 font-semibold hover:bg-orange-50 dark:hover:bg-gray-600 transition-colors"
          >
            📍 Get GPS Location
          </button>

          {value?.latitude && value?.longitude && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {`Lat: ${value.latitude.toFixed(4)}, Lon: ${value.longitude.toFixed(4)}`}
              </span>
              <button
                type="button"
                onClick={async () => {
                  const txt = `${value.latitude},${value.longitude}`;
                  try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(txt);
                    } else {
                      const ta = document.createElement('textarea');
                      ta.value = txt;
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                    }
                    // optional: small visual feedback could be added
                  } catch (e) {
                    console.warn('Copy failed', e);
                  }
                }}
                className="px-2 py-1 text-xs rounded border bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                title="Copy coordinates"
              >
                📋 Copy
              </button>
            </div>
          )}

        </div>

        {value?.latitude && value?.longitude && (
            <div className="mt-3 space-y-2 w-full">
              <div className="w-full h-40 rounded overflow-hidden border">
                <iframe
                  title="map"
                  src={`https://maps.google.com/maps?q=${value.latitude},${value.longitude}&z=15&output=embed`}
                  className="w-full h-full"
                />
              </div>
            </div>
        )}
      </BaseField>
    );
  }

  if (mode === "preset") {
    return (
      <BaseField
        field={field}
        value={value}
        setValue={setValue}
        openViewer={openViewer}
        renderInlineChildren={renderInlineChildren}
      >
        <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
          {label} {required && <span className="text-orange-500">*</span>}
        </h3>

        <select
          value={value?.preset ?? ""}
          onChange={(e) =>
            setValue({
              ...(value || {}),
              preset: e.target.value,
            })
          }
          className="w-full border-2 border-orange-500 rounded-lg p-3 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <option value="">Select location</option>
          {(field.config?.presets || []).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </BaseField>
    );
  }

  // Manual mode (default)
  return (
    <BaseField
      field={field}
      value={value}
      setValue={setValue}
      openViewer={openViewer}
      renderInlineChildren={renderInlineChildren}
      previewMode={previewMode}
    >
      <h3 className="text-gray-800 dark:text-gray-100 text-lg font-bold mb-3 text-center">
        {label} {required && <span className="text-orange-500">*</span>}
      </h3>

      <input
        type="text"
        value={value?.location ?? ""}
        onChange={(e) =>
          setValue({
            ...(value || {}),
            location: e.target.value,
          })
        }
        placeholder="Enter location name"
        className="w-full border-2 border-orange-500 rounded-lg p-3 bg-white dark:bg-gray-700 text-center text-base text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
      />
    </BaseField>
  );
}
